import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { getInquiryListApi } from '~/features/inquiry/api/get-inquiry-list.api';
import { getInquiryApi } from '~/features/inquiry/api/get-inquiry.api';

import type { PaginatedApiResponse } from '~/common/types/api.type';
import type { PaginationMeta } from '~/common/types/pagination.type';
import type { Inquiry, InquiryListParams } from '~/features/inquiry/types/inquiry.type';

export interface InquiryState {
  isLoading: boolean;
  list: Inquiry[];
  meta: PaginationMeta | null;
  params: InquiryListParams | null;
  selectedInquiry: Inquiry | null;
}

const initialState: InquiryState = {
  isLoading: false,
  list: [],
  meta: null,
  params: null,
  selectedInquiry: null,
};

/**
 * 문의 목록 조회
 */
export const fetchInquiryList = createAsyncThunk<
  PaginatedApiResponse<Inquiry>,
  InquiryListParams | undefined
>('inquiry/fetchList', async (params, { rejectWithValue }) => {
  try {
    const { data } = await getInquiryListApi(params);
    return data;
  } catch (err) {
    return rejectWithValue(err);
  }
});

/**
 * 문의 상세 조회
 */
export const fetchInquiryDetail = createAsyncThunk<Inquiry, number>(
  'inquiry/fetchDetail',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await getInquiryApi(id);
      return data.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

const InquirySlice = createSlice({
  initialState,
  name: 'inquiry',
  reducers: {
    setSelectedInquiry: (state, action) => {
      state.selectedInquiry = action.payload;
    },
    resetSelectedInquiry: state => {
      state.selectedInquiry = null;
    },
    resetInquiry: state => {
      state.selectedInquiry = null;
      state.list = [];
      state.meta = null;
      state.params = null;
      state.isLoading = false;
    },
  },
  extraReducers: builder => {
    builder
      // fetchInquiryList
      .addCase(fetchInquiryList.pending, (state, action) => {
        state.isLoading = true;
        state.params = action.meta.arg ?? null;
      })
      .addCase(fetchInquiryList.fulfilled, (state, action) => {
        state.list = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.meta = action.payload.meta ?? null;
        state.isLoading = false;
      })
      .addCase(fetchInquiryList.rejected, state => {
        state.isLoading = false;
      })
      // fetchInquiryDetail
      .addCase(fetchInquiryDetail.pending, state => {
        state.isLoading = true;
      })
      .addCase(fetchInquiryDetail.fulfilled, (state, action) => {
        state.selectedInquiry = action.payload;
        state.isLoading = false;
      })
      .addCase(fetchInquiryDetail.rejected, state => {
        state.isLoading = false;
      });
  },
});

export const { setSelectedInquiry, resetSelectedInquiry, resetInquiry } = InquirySlice.actions;
export default InquirySlice;
